import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { getTemplateBySlug, getAllTemplateSlugs, SEO_TEMPLATES } from '../data/seoTemplates';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, CheckCircle, ArrowRight, Briefcase, 
  Target, Lightbulb, Award, Users, ChevronRight, Shield, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ResumeTemplateLanding = () => {
  const { roleSlug } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [templateData, setTemplateData] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Load template data based on slug
  useEffect(() => {
    const data = getTemplateBySlug(roleSlug);
    if (data) {
      setTemplateData(data);
      setNotFound(false);
      
      // Fetch template preview
      const fetchPreview = async () => {
        try {
          const response = await fetch(`${API}/resumes/templates/${data.templateId}/preview`);
          if (response.ok) {
            const html = await response.text();
            setTemplatePreview(html);
          }
        } catch (error) {
          console.log('Preview fetch error:', error);
        }
      };
      fetchPreview();
    } else {
      setNotFound(true);
    }
  }, [roleSlug]);

  // Handle CTA click
  const handleStartBuilding = () => {
    if (user) {
      // User is logged in - go directly to convert page
      navigate('/convert');
    } else {
      // User not logged in - redirect to login
      navigate('/login');
    }
  };

  // Set document title using useEffect
  useEffect(() => {
    if (templateData?.metaTitle) {
      document.title = templateData.metaTitle;
    }
  }, [templateData]);

  // Get related templates
  const getRelatedTemplates = () => {
    if (!templateData?.relatedRoles) return [];
    return templateData.relatedRoles
      .map(slug => SEO_TEMPLATES[slug])
      .filter(Boolean)
      .slice(0, 3);
  };

  // 404 page for invalid slugs
  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Template Not Found</h1>
          <p className="text-slate-600 mb-6">The resume template you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/login')} data-testid="browse-templates-btn">
            Browse All Templates
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (!templateData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  const relatedTemplates = getRelatedTemplates();

  return (
    <>
      {/* SEO Meta Tags - Only render when templateData is available */}
      {templateData && templateData.metaDescription && (
        <Helmet>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.atsKeywords?.join(', ') || ''} />
          <meta property="og:title" content={templateData.metaTitle} />
          <meta property="og:description" content={templateData.metaDescription} />
          <meta property="og:type" content="website" />
          <link rel="canonical" href={`https://fixresume24.com/resume-template/${templateData.slug}`} />
        </Helmet>
      )}

      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
        {/* Header */}
        <header className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/login" className="inline-flex items-center space-x-2" data-testid="logo-link">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <FileText className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-lg text-primary">FixResume24</span>
            </Link>
            <Button 
              onClick={handleStartBuilding}
              size="sm"
              data-testid="header-cta-btn"
            >
              Get Started Free
            </Button>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-12 md:py-16 px-4" data-testid="hero-section">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left: Content */}
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  <Briefcase className="h-4 w-4" />
                  <span>{templateData.heroTitle} Resume</span>
                </div>
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight" data-testid="hero-title">
                  {templateData.title}
                </h1>
                
                <p className="text-lg text-slate-600 max-w-xl">
                  {templateData.heroSubtitle}
                </p>

                {/* Key Stats */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg">
                    <Target className="h-5 w-5" />
                    <div>
                      <p className="text-xs font-medium">Market Demand</p>
                      <p className="font-bold">{templateData.demandLevel}</p>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    size="lg" 
                    className="text-base px-8"
                    onClick={handleStartBuilding}
                    data-testid="hero-cta-btn"
                  >
                    Start Building This Resume
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>No credit card required</span>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Badge variant="outline" className="text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    ATS-Optimized
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Sparkles className="h-3 w-3 mr-1" />
                    AI-Powered
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <FileText className="h-3 w-3 mr-1" />
                    29+ Templates
                  </Badge>
                </div>
              </div>

              {/* Right: Template Preview */}
              <div className="hidden lg:block">
                <Card className="overflow-hidden shadow-2xl border-slate-200" data-testid="template-preview">
                  <CardContent className="p-0">
                    <div className="h-[500px] overflow-hidden bg-white">
                      {templatePreview ? (
                        <div 
                          className="transform scale-[0.5] origin-top-left w-[200%] h-[200%] pointer-events-none"
                          dangerouslySetInnerHTML={{ __html: templatePreview }}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center bg-slate-50">
                          <div className="text-center text-slate-400">
                            <FileText className="h-16 w-16 mx-auto mb-2 opacity-30" />
                            <p>Template Preview</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Job Description Section */}
        <section className="py-12 bg-white border-y border-slate-100" data-testid="job-description-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-3xl">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                What Does a {templateData.heroTitle} Do?
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg">
                {templateData.jobDescription}
              </p>
            </div>
          </div>
        </section>

        {/* Key Skills Section */}
        <section className="py-12 px-4" data-testid="skills-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">
              Essential Skills for {templateData.heroTitle} Resumes
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {templateData.keySkills.map((skill, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 bg-white p-4 rounded-lg border border-slate-200 hover:border-primary/50 transition-colors"
                >
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{skill}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ATS Keywords Section */}
        <section className="py-12 bg-slate-50 px-4" data-testid="keywords-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <Target className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold text-slate-900">
                ATS Keywords to Include
              </h2>
            </div>
            <p className="text-slate-600 mb-6 max-w-2xl">
              Applicant Tracking Systems scan for these keywords. Make sure your resume includes relevant terms to improve your chances of getting past the initial screening.
            </p>
            <div className="flex flex-wrap gap-2">
              {templateData.atsKeywords.map((keyword, index) => (
                <Badge 
                  key={index}
                  variant="secondary"
                  className="text-sm py-1.5 px-3 bg-white border border-slate-200"
                >
                  {keyword}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* Summary Examples Section */}
        <section className="py-12 px-4" data-testid="summary-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <Lightbulb className="h-6 w-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-slate-900">
                Professional Summary Examples
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {templateData.summaryExamples.map((summary, index) => (
                <Card key={index} className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-primary flex items-center gap-2">
                      <Award className="h-4 w-4" />
                      Example {index + 1}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 italic">"{summary}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Recruiter Tips Section */}
        <section className="py-12 bg-primary/5 px-4" data-testid="tips-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <Users className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold text-slate-900">
                Tips From Recruiters
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templateData.recruiterTips.map((tip, index) => (
                <div 
                  key={index}
                  className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <p className="text-slate-700">{tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-primary to-primary/80" data-testid="cta-section">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Build Your {templateData.heroTitle} Resume?
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Join thousands of professionals who've landed their dream jobs with FixResume24.
            </p>
            <Button 
              size="lg" 
              variant="secondary"
              className="text-base px-8 bg-white text-primary hover:bg-slate-100"
              onClick={handleStartBuilding}
              data-testid="bottom-cta-btn"
            >
              Start Building for Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Related Templates Section */}
        {relatedTemplates.length > 0 && (
          <section className="py-12 px-4" data-testid="related-section">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Explore Related Resume Templates
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedTemplates.map((template) => (
                  <Link 
                    key={template.slug}
                    to={`/resume-template/${template.slug}`}
                    className="block"
                    data-testid={`related-template-${template.slug}`}
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow border-slate-200 hover:border-primary/50">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-primary mb-2">
                          <Briefcase className="h-4 w-4" />
                          <span className="text-sm font-medium">{template.heroTitle}</span>
                        </div>
                        <h3 className="font-bold text-slate-900 mb-2">{template.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                          {template.heroSubtitle}
                        </p>
                        <div className="flex items-center text-primary text-sm font-medium">
                          View Template
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="py-8 border-t border-slate-100 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <span className="font-bold text-primary">FixResume24</span>
              </div>
              <div className="flex gap-6 text-sm text-slate-600">
                <Link to="/privacy-policy" className="hover:text-primary">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-primary">Terms of Service</Link>
              </div>
              <p className="text-sm text-slate-500">
                © {new Date().getFullYear()} FixResume24. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ResumeTemplateLanding;
